import React, { useEffect } from 'react';
import { useParams, Link, Navigate, useSearchParams } from 'react-router-dom';
import { getProjects } from '../data/projects';
import { categories } from '../data/products';
import { ArrowRight, CheckCircle2, ChevronRight, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { JsonLd, useSEO } from '../utils/seo';
export function Project() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const projects = getProjects();
  const project = projects.find(p => p.slug === slug);
  const activeIndex = projects.findIndex(p => p.slug === slug);
  // Every guide starts COLLAPSED. With one guide expanded on arrival, its long
  // detail panel pushed the remaining cards far off screen and the page looked
  // like a single project rather than a browsable index. The customer opens the
  // one they want; tapping it again closes it.
  // ...unless the customer arrived from a direct "open this guide" link (the
  // project pills in the Materials Hub), which passes ?open=1 so the guide they
  // picked is already expanded on arrival.
  // Expanded/collapsed lives in the URL (?open=1), NOT in component state.
  // Switching guides changes the route, and the router remounts this page on
  // that change — local state was resetting to "collapsed" on every card tap,
  // which is why the first tap looked like it did nothing and a second tap was
  // needed. Reading it from the URL survives the remount.
  const [searchParams] = useSearchParams();
  const isExpanded = searchParams.get('open') === '1';
  // Collapsed: land at the top so the index reads as a browsable list.
  // Expanded: scroll to the panel that just opened, after paint so the
  // measurement is taken against the final layout.
  useEffect(() => {
    if (!isExpanded) {
      window.scrollTo(0, 0);
      return;
    }
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById('project-detail');
      if (!el) return;
      const navHeight = document.querySelector('nav')?.offsetHeight || 80;
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY - navHeight - 12,
        behavior: 'smooth'
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [slug, isExpanded]);
  const projectDescription = project ? `${project.tagline} See recommended materials, depths, and practical tips for a ${project.name.toLowerCase()} project in the Puget Sound area.` : 'Explore practical gravel, drainage, patio, and landscaping project guides from 206Gravel.';
  useSEO({
    title: project ? `${project.name} Materials & Pro Tips | 206Gravel` : 'Gravel Project Guides | 206Gravel',
    description: projectDescription,
    path: project ? `/projects/${project.slug}` : '/'
  });
  if (!project) {
    return <Navigate to="/" replace data-id="element-3363" data-mp="29zv88dwh2yot" />;
  }
  const Icon = project.icon;
  // Helper to find full product details from data/products.ts
  const getProductDetails = (productSlug: string) => {
    for (const cat of categories) {
      const product = cat.products.find(p => p.slug === productSlug);
      if (product) {
        return {
          ...product,
          categoryName: cat.name
        };
      }
    }
    return null;
  };
  return <main className="min-h-screen bg-[#F2EFE8] dark:bg-[#262626] pt-8 pb-24" data-id="element-3364" data-mp="1xplx923wjtqx">
      <JsonLd data={[{
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: project.name,
      description: projectDescription,
      url: `https://206gravel.com/projects/${project.slug}`,
      supply: project.materials.map(material => {
        const product = getProductDetails(material.slug);
        return {
          '@type': 'HowToSupply',
          name: product ? product.name : material.role,
          ...(product ? {
            url: `https://206gravel.com/shop/${product.slug}`,
            image: product.image
          } : {})
        };
      }),
      step: project.tips.map((tip, index) => ({
        '@type': 'HowToStep',
        position: index + 1,
        name: `Step ${index + 1}`,
        text: tip,
        url: `https://206gravel.com/projects/${project.slug}#step-${index + 1}`
      }))
    }, {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: project.faqs.map(faq => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a
        }
      }))
    }, {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://206gravel.com/'
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Shop by Project',
        item: 'https://206gravel.com/projects/new-gravel-driveway'
      }, {
        '@type': 'ListItem',
        position: 3,
        name: project.name,
        item: `https://206gravel.com/projects/${project.slug}`
      }]
    }]} data-id="element-3365" data-mp="1sr29qx9nu9wg" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3366" data-mp="9a5qf2mfw7ya">
        {/* Breadcrumbs */}
        <nav className="flex items-center text-sm text-stone-500 dark:text-stone-400 mb-6" aria-label="Breadcrumb" data-id="element-3367" data-mp="9byzeokiz49g">
          <Link to="/" className="hover:text-brand-orange transition-colors" data-id="element-3368" data-mp="iakbq1zeln1s">
            Home
          </Link>
          <ChevronRight className="w-4 h-4 mx-2 shrink-0" data-id="element-3369" data-mp="e8pg9e45m3vx" />
          <Link to="/projects/new-gravel-driveway" className="hover:text-brand-orange transition-colors whitespace-nowrap" data-id="element-3370" data-mp="e74lpohuaky2">
            Shop by Project
          </Link>
          <ChevronRight className="w-4 h-4 mx-2 shrink-0" data-id="element-3371" data-mp="3hr8zjr4utzel" />
          <span className="text-stone-900 dark:text-white font-medium truncate" data-id="element-3372" data-mp="3vh42a7d3vpjv">
            {project.name}
          </span>
        </nav>

        {/* Project switcher — mirrors the Materials Hub: an accent rule, a
            large section heading, and a grid of photo-backed cards. The old
            icon-tile tabs read as navigation chrome; these read as browsable
            guides, which is what a customer who doesn't know their material is
            actually looking for. The photo comes from each guide's primary
            material, so the card shows the real thing being delivered. */}
        <section className="mb-14" aria-label="Project guides" data-id="element-3373" data-mp="1bo3jqb50qcti">
          <motion.div initial={{
          width: 0,
          opacity: 0
        }} whileInView={{
          width: 64,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.65,
          ease: [0.22, 1, 0.36, 1]
        }} className="mb-6 h-1 rounded-full bg-brand-orange" data-id="element-3374" data-mp="1bqpz2wy5hwma" />
          <div className="mb-5 flex flex-col gap-2 md:flex-row md:items-baseline md:gap-6" data-id="element-3375" data-mp="sxm3v75abbpk">
            <h2 className="font-heading text-4xl font-extrabold leading-[1.1] tracking-tight text-brand-charcoal dark:text-white md:text-5xl" data-id="element-3376" data-mp="xwynzk4ru4og">
              Project <span className="font-black text-[#04A05C]" data-id="element-3377" data-mp="24d10uaxhu3ko">Guides</span>
            </h2>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#17A348]" data-id="element-3378" data-mp="29b2n14q7uj0d">
              — Browse by project
            </span>
          </div>
          <p className="mb-8 max-w-2xl font-sub text-lg font-medium leading-relaxed tracking-wide text-brand-charcoal dark:text-white" data-id="element-3379" data-mp="3ovl7nkgk9oow">
            Pick the job you're doing and we'll show the exact materials, depths,
            and quantities that job needs.
          </p>

          <div className="grid grid-cols-1 gap-[22px] sm:grid-cols-2 sm:gap-[26px] md:grid-cols-3" data-id="element-3380" data-mp="3f3p9yaie00l7">
            {projects.map((p, index) => {
            const isActive = p.slug === project.slug;
            const heroMaterial = getProductDetails(p.materials[0]?.slug ?? '');
            return <motion.div key={p.slug} style={{
              order: index * 2
            }} initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.22,
              delay: Math.min(index, 5) * 0.035
            }} data-id="element-3381" data-mp="317v0nqdpb97k" data-mpi={index}>
                  <Link to={isActive && isExpanded ? `/projects/${p.slug}` : `/projects/${p.slug}?open=1`} aria-current={isActive ? 'page' : undefined} aria-expanded={isActive ? isExpanded : false} className={`group relative block h-[13.2rem] overflow-hidden rounded-lg border bg-stone-200 shadow-sm transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] focus-visible:ring-offset-2 hover:shadow-md sm:h-48 dark:bg-white/10 dark:focus-visible:ring-offset-[#262626] ${isActive && isExpanded ? 'border-[#04A05C] ring-2 ring-[#04A05C]' : 'border-stone-200 dark:border-white/10'}`} data-id="element-3382" data-mp="39ovzm2izc7d">
                    {heroMaterial && <img src={heroMaterial.images?.[0] || heroMaterial.image} alt="" loading={index < 3 ? 'eager' : 'lazy'} decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" data-id="element-3383" data-mp="36s8evxzbd5pi" />}
                    {isActive && isExpanded && <span className="absolute left-0 top-4 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wide text-[#fffcf9] shadow-sm bg-[#006239]" data-id="element-3384" data-mp="19xcbr5xgxap0">
                        Open — tap to close
                      </span>}
                    <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 bg-[#FBFCFB] px-3 py-2.5 text-stone-650 sm:px-4 dark:bg-black/65 dark:text-white dark:backdrop-blur-[1px]" data-id="element-3385" data-mp="2pf9y16mq4ikj">
                      <div className="min-w-0 font-sub" data-id="element-3386" data-mp="2zdpqpe1efzw5">
                        <p className="truncate text-[9px] uppercase tracking-[0.12em] text-stone-900 dark:text-white/80 font-[400] font-[Cabin,_sans-serif]" data-id="element-3387" data-mp="3d5mex6zy5pn9">
                          Project guide
                        </p>
                        <h3 className="mt-0.5 truncate font-[Cabin,_sans-serif] text-[18px] font-[500] sm:text-base" data-id="element-3388" data-mp="3n4bv8vc7321e">
                          {p.name}
                        </h3>
                        <p className="mt-0.5 truncate text-[11px] leading-snug text-stone-600 dark:text-white/70 font-[Cabin,_sans-serif]" data-id="element-3389" data-mp="1srm3ok9au4x6">
                          {p.materials.length} {p.materials.length === 1 ? 'material' : 'materials'} · {p.typicalYards}
                        </p>
                      </div>
                    </div>
                  </Link>
                </motion.div>;
          })}

            {/* The selected guide expands INSIDE the grid, as a full-width row
                ordered to fall immediately after its own card (cards take even
                orders, this panel takes the odd slot right after the active
                one). On mobile that means the content opens directly under the
                card the customer just tapped instead of below all seven. */}
            <div id="project-detail" hidden={!isExpanded} className="col-span-full scroll-mt-24" style={{
            order: activeIndex * 2 + 1
          }} data-id="element-3390" data-mp="bufpc65hrhxq">
        {/* Header */}
        <div className="mb-12" data-id="element-3391" data-mp="14cjqjzba1yi5">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-orange/10 text-brand-orange mb-6" data-id="element-3392" data-mp="14dkyhuwzy4vn">
            <Icon className="w-8 h-8" data-id="element-3393" data-mp="2i8ba4c9pv2s5" />
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-black text-stone-900 dark:text-white mb-4" data-id="element-3394" data-mp="2i9w1xu4nc7mb">
            {project.name}
          </h1>
          <p className="text-xl md:text-2xl text-stone-600 dark:text-stone-300 font-medium mb-6" data-id="element-3395" data-mp="2r99c6p0lh083">
            {project.tagline}
          </p>
          <div className="prose prose-lg dark:prose-invert text-stone-600 dark:text-stone-400 max-w-none" data-id="element-3396" data-mp="2w8vql1s5smih">
            <p data-id="element-3397" data-mp="316nrumep1kzc">{project.intro}</p>
          </div>
          <div className="mt-6 inline-flex items-center px-4 py-2 rounded-full bg-stone-100 dark:bg-white/5 text-stone-700 dark:text-stone-300 text-sm font-medium border border-stone-200 dark:border-white/10" data-id="element-3398" data-mp="3660gncp4l24k">
            <span className="mr-2" data-id="element-3399" data-mp="3ez3sjgk4h6x3">Typical project size:</span>
            <span className="font-bold text-stone-900 dark:text-white" data-id="element-3400" data-mp="3f0etl8m6o0ki">
              {project.typicalYards}
            </span>
          </div>
        </div>

        {/* Materials Section */}
        <div className="mb-16" data-id="element-3401" data-mp="3ounkxszh9nh3">
          <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6" data-id="element-3402" data-mp="1ou8rz8kp0swy">
            Recommended Materials
          </h2>
          {/* Materials read as an ordered SPEC LIST, not cards. Rounded,
              shadowed, photo-led cards looked identical to the project tiles
              above, so the page appeared to be showing more projects. Square
              edges, shared rules between rows, a step number, and a small
              square thumbnail make these read as the layers of one job. */}
          <div className="border-t border-stone-300 dark:border-white/15" data-id="element-3403" data-mp="23id5ly4w2e8v">
            {project.materials.map((mat, idx) => {
                  const product = getProductDetails(mat.slug);
                  if (!product) return null;
                  return <motion.div key={mat.slug} initial={{
                    opacity: 0,
                    y: 12
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: idx * 0.06
                  }} className="flex gap-4 border-b border-stone-300 py-6 sm:gap-6 dark:border-white/15" data-id="element-3404" data-mp="2ckw2m1lztwn7" data-mpi={idx}>
                  <div className="flex shrink-0 flex-col items-center gap-3" data-id="element-3405" data-mp="2ys8lvfbnxr6c">
                    <span className="flex h-7 w-7 items-center justify-center bg-stone-900 text-xs font-bold text-white dark:bg-white dark:text-stone-900" data-id="element-3406" data-mp="2yucnfqm3ae3g">
                      {idx + 1}
                    </span>
                    <img src={product.image} alt={product.name} loading="lazy" decoding="async" className="h-20 w-20 object-cover sm:h-24 sm:w-24" data-id="element-3407" data-mp="2b2xhlnv3teng" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-3408" data-mp="2g3bo31h24gjd">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1" data-id="element-3409" data-mp="215jdmnqdaic1">
                      <h3 className="font-heading text-xl font-bold text-stone-900 dark:text-white" data-id="element-3410" data-mp="2631dlomepn7w">
                        {product.name}
                      </h3>
                      <div className="font-heading text-lg font-bold tabular-nums text-stone-900 dark:text-white" data-id="element-3411" data-mp="4chik9ep2ky7">
                        ${product.pricePerYard}
                        <span className="text-sm font-normal text-stone-500 dark:text-stone-400" data-id="element-3412" data-mp="20f2sg9g2geuq">
                          /yd
                        </span>
                      </div>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-bold uppercase tracking-wider" data-id="element-3413" data-mp="1rd3bti8yrcsb">
                      <span className="text-brand-orange" data-id="element-3414" data-mp="1hgi4gq9h1plb">{mat.role}</span>
                      <span className="text-stone-300 dark:text-stone-600" data-id="element-3415" data-mp="1hil31yoowr1n">·</span>
                      <span className="text-stone-500 dark:text-stone-400" data-id="element-3416" data-mp="1coqn96c7gw7u">
                        Depth: {mat.depth}
                      </span>
                    </div>
                    <p className="mt-2.5 text-stone-600 dark:text-stone-300" data-id="element-3417" data-mp="13qeq0lgu306f">
                      <span className="font-semibold text-stone-900 dark:text-white" data-id="element-3418" data-mp="yqsc09t6lfwk">
                        Why:{' '}
                      </span>
                      {mat.why}
                    </p>
                    <Link to={`/shop/${product.slug}`} className="mt-3 inline-flex items-center text-sm font-bold text-brand-orange transition-colors hover:text-brand-orange/80" data-id="element-3419" data-mp="jo0j6wu4186b">
                      View product details
                      <ArrowRight className="w-4 h-4 ml-1" data-id="element-3420" data-mp="tmq1gqpx4un3" />
                    </Link>
                  </div>
                </motion.div>;
                })}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12" data-id="element-3421" data-mp="xiwp9zvf8te">
          {/* Pro Tips */}
          <div data-id="element-3422" data-mp="5vkmsevps8ed">
            <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6 flex items-center" data-id="element-3423" data-mp="5v195ad3k665">
              <CheckCircle2 className="w-6 h-6 mr-2 text-[#04A05C]" data-id="element-3424" data-mp="au5fzucjtrtb" />
              Pro Tips
            </h2>
            <ul className="space-y-4" data-id="element-3425" data-mp="fn6va3go3tdf">
              {project.tips.map((tip, idx) => <li key={idx} id={`step-${idx + 1}`} className="flex items-start scroll-mt-24" data-id="element-3426" data-mp="3ecfh35yg4kgu" data-mpi={idx}>
                  <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-brand-orange mt-2 mr-3" data-id="element-3427" data-mp="3j9xga4mnvlbn" />
                  <span className="text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-3428" data-mp="3b99g8apomvjr">
                    {tip}
                  </span>
                </li>)}
            </ul>
          </div>

          {/* FAQs */}
          <div data-id="element-3429" data-mp="3kyj6dl0yu458">
            <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6 flex items-center" data-id="element-3430" data-mp="3kz2n4yevqmho">
              <HelpCircle className="w-6 h-6 mr-2 text-brand-orange" data-id="element-3431" data-mp="3g2ubhuzw5bf9" />
              Project FAQs
            </h2>
            <div className="space-y-6" data-id="element-3432" data-mp="2nc3owj8jh6ho">
              {project.faqs.map((faq, idx) => <div key={idx} data-id="element-3433" data-mp="2ndwz28id28uf" data-mpi={idx}>
                  <h4 className="font-bold text-stone-900 dark:text-white mb-2" data-id="element-3434" data-mp="32933ws5q01m6">
                    {faq.q}
                  </h4>
                  <p className="text-stone-600 dark:text-stone-300 text-sm leading-relaxed" data-id="element-3435" data-mp="2x98b2j2dx1yh">
                    {faq.a}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 rounded-2xl p-8 md:p-12 text-center bg-[#1a1a1a]" style={{
              backgroundColor: "#575656"
            }} data-id="element-3436" data-mp="cjrekdgkpdod">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4" data-id="element-3437" data-mp="k5wh7hpzfcvj">
            Ready to start your project?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto" data-id="element-3438" data-mp="b3x2wx6cqmwa">
            Get an instant estimate including delivery for all the materials you
            need.
          </p>
          <Link to={`/?from=project&project=${project.slug}#estimator`} className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-brand-orange hover:bg-brand-orange/90 rounded-xl transition-colors" data-id="element-3439" data-mp="12ub721okckur">
            Get an Estimate
            <ArrowRight className="w-5 h-5 ml-2" data-id="element-3440" data-mp="tzn44i6gnf94" />
          </Link>
        </div>
            </div>
          </div>
        </section>
      </div>
    </main>;
}